import { useMemo, useState } from 'react';
import { ActivityIndicator, StyleSheet, Text, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/Button';
import { fontSize, radius, spacing } from '@/constants/theme';
import type { AppColors } from '@/constants/theme';
import { useTheme } from '@/contexts/ThemeContext';
import { supabase } from '@/lib/supabase';

type Props = {
  agreementId: string;
  isCreator: boolean;
  creatorWants: boolean | null;
  counterpartyWants: boolean | null;
  onRenewed: (newAgreementId: string) => void;
  onDeclined: () => void;
};

export function RenewalBanner({ agreementId, isCreator, creatorWants, counterpartyWants, onRenewed, onDeclined }: Props) {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const [loading, setLoading] = useState(false);
  const [responded, setResponded] = useState(false);

  const myVote = isCreator ? creatorWants : counterpartyWants;
  const otherVote = isCreator ? counterpartyWants : creatorWants;

  async function respond(accept: boolean) {
    setLoading(true);
    try {
      const { data, error } = await supabase.rpc('respond_renewal', {
        p_agreement_id: agreementId,
        p_accept: accept,
      });
      if (error) throw error;

      const result = data as { renewed: boolean; waiting: boolean; new_agreement_id?: string };
      if (result.renewed && result.new_agreement_id) {
        onRenewed(result.new_agreement_id);
      } else if (!accept) {
        onDeclined();
      } else {
        setResponded(true);
      }
    } catch (e) {
      console.error('respond_renewal error', e);
    } finally {
      setLoading(false);
    }
  }

  // Αν ήδη ψήφισα, δείχνω κατάσταση αναμονής
  if (myVote !== null || responded) {
    return (
      <View style={styles.banner}>
        <Ionicons name="time-outline" size={18} color={colors.warning} />
        <View style={styles.info}>
          <Text style={styles.title}>{t('renewal.waitingTitle')}</Text>
          <Text style={styles.sub}>
            {myVote === true
              ? t('renewal.youAccepted')
              : myVote === false
              ? t('renewal.youDeclined')
              : t('renewal.youAccepted')}
          </Text>
          {otherVote === false && (
            <Text style={[styles.sub, { color: colors.danger }]}>{t('renewal.otherDeclined')}</Text>
          )}
        </View>
      </View>
    );
  }

  return (
    <View style={styles.banner}>
      <Ionicons name="refresh-circle-outline" size={20} color={colors.primary} />
      <View style={styles.info}>
        <Text style={styles.title}>{t('renewal.promptTitle')}</Text>
        <Text style={styles.sub}>
          {t('renewal.promptBody')}
        </Text>
        {otherVote === true && (
          <Text style={[styles.sub, { color: colors.success }]}>{t('renewal.otherAccepted')}</Text>
        )}
        {loading ? (
          <ActivityIndicator color={colors.primary} style={{ marginTop: spacing.sm }} />
        ) : (
          <View style={styles.buttons}>
            <Button title={t('renewal.action_yes')} icon="checkmark-outline" onPress={() => respond(true)} />
            <Button title={t('renewal.action_no')} variant="secondary" onPress={() => respond(false)} />
          </View>
        )}
      </View>
    </View>
  );
}

function makeStyles(colors: AppColors) {
  return StyleSheet.create({
    banner: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      gap: spacing.sm,
      backgroundColor: `${colors.primary}10`,
      borderWidth: 1,
      borderColor: `${colors.primary}30`,
      borderRadius: radius.md,
      padding: spacing.md,
    },
    info: { flex: 1, gap: spacing.xs },
    title: {
      color: colors.text,
      fontSize: fontSize.sm,
      fontWeight: '700',
    },
    sub: {
      color: colors.textMuted,
      fontSize: fontSize.sm,
      lineHeight: 18,
    },
    buttons: {
      gap: spacing.sm,
      marginTop: spacing.sm,
    },
  });
}
